import React, { useState, useEffect, useRef, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';

export default function InvoiceNumberDialog({
  open,
  suggestedInvoiceNumber,
  entityPrefix,
  currentCounter,
  availableGaps,
  onConfirm,
  onClose,
  loading,
}) {
  const [option, setOption] = useState('auto');
  const [numInput, setNumInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [existsCheck, setExistsCheck] = useState(null); // { exists: boolean } | null
  const [inputError, setInputError] = useState('');
  const debounceRef = useRef(null);

  const gaps = useMemo(() => availableGaps || [], [availableGaps]);
  const counter = currentCounter ?? 0;
  const prefix = entityPrefix || '';

  useEffect(() => {
    if (open) {
      setOption('auto');
      setNumInput('');
      setChecking(false);
      setExistsCheck(null);
      setInputError('');
    }
  }, [open]);

  const parsedNum = useMemo(() => {
    const n = parseInt(numInput, 10);
    return !isNaN(n) && n > 0 ? n : null;
  }, [numInput]);

  // Classify what kind of entry the user made
  const entryInfo = useMemo(() => {
    if (parsedNum === null) return null;
    if (parsedNum <= counter) {
      return gaps.includes(parsedNum)
        ? { type: 'gap', ok: true }
        : { type: 'backfill', ok: true };
    }
    const gapsCreated = parsedNum - counter - 1;
    return { type: 'advance', ok: true, gapsCreated, newCounter: parsedNum };
  }, [parsedNum, counter, gaps]);

  const fullCustomInvoice = useMemo(
    () => (prefix && parsedNum ? `${prefix}-${String(parsedNum).padStart(6, '0')}` : ''),
    [prefix, parsedNum]
  );

  const triggerCheck = (n) => {
    const invoice = `${prefix}-${String(n).padStart(6, '0')}`;
    setChecking(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const resp = await fetch(
          `/api-dev/expenses/checkInvoiceNumber?invoiceNumber=${encodeURIComponent(invoice)}`
        );
        const data = await resp.json();
        setExistsCheck(data);
      } catch {
        // ignore check failures silently
      } finally {
        setChecking(false);
      }
    }, 400);
  };

  const handleNumChange = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    setNumInput(val);
    setExistsCheck(null);
    setInputError('');
    clearTimeout(debounceRef.current);
    if (!val) return;
    const n = parseInt(val, 10);
    if (isNaN(n) || n <= 0) {
      setInputError('Please enter a valid positive number.');
      return;
    }
    triggerCheck(n);
  };

  const handleGapPick = (n) => {
    setNumInput(String(n));
    setExistsCheck(null);
    setInputError('');
    triggerCheck(n);
  };

  const isAlreadyUsed = existsCheck?.exists === true;

  const isCustomValid =
    parsedNum !== null &&
    !inputError &&
    !checking &&
    !isAlreadyUsed &&
    entryInfo?.ok === true;

  const isConfirmDisabled = loading || (option === 'custom' && !isCustomValid);

  const handleConfirm = () => {
    if (option === 'auto') onConfirm('auto');
    else if (option === 'none') onConfirm('none');
    else onConfirm(fullCustomInvoice);
  };

  const helperText = () => {
    if (!numInput) return `Enter the sequence number (current counter: ${counter})`;
    if (inputError) return inputError;
    if (checking) return 'Checking availability…';
    if (isAlreadyUsed) return 'This invoice number already exists.';
    if (!entryInfo) return '';
    if (entryInfo.type === 'gap')
      return 'Available — gap fill (this number was previously skipped).';
    if (entryInfo.type === 'backfill')
      return `Available — counter will not change.`;
    if (entryInfo.gapsCreated === 0)
      return 'Available — sequential (same as the suggested number).';
    return `Available — counter will advance from ${counter} to ${parsedNum}, creating ${entryInfo.gapsCreated} gap${entryInfo.gapsCreated > 1 ? 's' : ''}.`;
  };

  const helperColor = () => {
    if (inputError || isAlreadyUsed) return 'error.main';
    if (entryInfo?.type === 'gap') return 'success.main';
    if (entryInfo?.type === 'backfill') return 'success.main';
    if (entryInfo?.type === 'advance' && entryInfo.gapsCreated > 0) return 'warning.main';
    if (entryInfo?.type === 'advance') return 'success.main';
    return 'text.secondary';
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Internal Invoice Number</DialogTitle>
      <DialogContent>
        <RadioGroup value={option} onChange={(e) => setOption(e.target.value)}>
          {/* Option 1 — Accept suggested */}
          <FormControlLabel
            value="auto"
            control={<Radio />}
            label={
              <span>
                Accept suggested: <strong>{suggestedInvoiceNumber || '…'}</strong>
              </span>
            }
          />

          {/* Option 2 — Custom */}
          <FormControlLabel
            value="custom"
            control={<Radio />}
            label="Enter a custom invoice number"
          />
          {option === 'custom' && (
            <Box sx={{ ml: 4, mt: 0.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Current counter: <strong>{counter}</strong>
                {counter > 0 && (
                  <>
                    {' '}— next auto-suggested:{' '}
                    <strong style={{ fontFamily: 'monospace' }}>{suggestedInvoiceNumber}</strong>
                  </>
                )}
              </Typography>

              {/* Prefix + number input */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    mt: '8px',
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                    color: 'text.primary',
                  }}
                >
                  {prefix}&nbsp;-
                </Typography>
                <TextField
                  size="small"
                  value={numInput}
                  onChange={handleNumChange}
                  placeholder="e.g. 24"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  error={!!inputError || isAlreadyUsed}
                  helperText={
                    <span style={{ color: 'inherit' }}>{helperText()}</span>
                  }
                  FormHelperTextProps={{ sx: { color: helperColor() } }}
                  InputProps={{
                    endAdornment: checking ? (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{ width: 160 }}
                />
                {fullCustomInvoice && !checking && !inputError && !isAlreadyUsed && entryInfo?.ok && (
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: 'monospace', color: 'text.secondary', mt: '10px' }}
                  >
                    → {fullCustomInvoice}
                  </Typography>
                )}
              </Box>

              {/* Counter-advance warning */}
              {entryInfo?.type === 'advance' && entryInfo.gapsCreated > 0 && !checking && (
                <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, fontSize: '0.8rem' }}>
                  This will advance the counter to <strong>{parsedNum}</strong> and create{' '}
                  <strong>{entryInfo.gapsCreated}</strong> gap
                  {entryInfo.gapsCreated > 1 ? 's' : ''} (
                  {counter + 1}–{parsedNum - 1}).
                </Alert>
              )}

            </Box>
          )}

          {/* Option 3 — No invoice number */}
          <FormControlLabel
            value="none"
            control={<Radio />}
            label="No invoice number"
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={isConfirmDisabled}>
          {loading ? <CircularProgress size={18} /> : 'Confirm & Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

